'use client';

import { Box, Button, Chip, LinearProgress, Paper, Stack, Tooltip, Typography } from '@mui/material';
import type { AppColors } from '@/styles/colors';
import { InfoDotTooltip } from './TickerPortfolioTabVisuals';
import type { PortfolioTabViewModel, PortfolioMetric, SectionId, CompositionItem } from './TickerPortfolioTabClient';
import { AppLink } from '@/components/mui/app-link';

type CompositionViewItem = CompositionItem & {
  absValue: number;
  color: string;
  isNegative: boolean;
};

type Props = {
  data: PortfolioTabViewModel;
  isDark: boolean;
  compositionPositiveItems: CompositionViewItem[];
  compositionNegativeItems: CompositionViewItem[];
  compositionPositiveTotal: number;
  compositionNegativeTotal: number;
  compositionAbsTotal: number;
  compositionDonutBackground: string;
  compositionNegativeDonutBackground: string;
  /**
   * Kept for backwards compatibility — the default behaviour preserves the
   * visual click on a composition "Ver" link. The path is now resolved via
   * {@link resolveCompositionHref} so callers don't need to keep this in
   * sync with the URL contract.
   */
  onGoToSection?: (section: Exclude<SectionId, 'overview'>) => void;
  onGoToCashTab: () => void;
  resolveCompositionHref: (item: CompositionItem) => string | null;
  isCashCompositionItem: (label: string) => boolean;
  metricAccentColor: (label: string, colors: AppColors) => string;
  appColors: AppColors;
  metricInfoText: (label: string) => string;
  normalizeMetricLabel: (value: string) => string;
  formatMetricValue: (value: number | null, kind: PortfolioMetric['kind']) => string;
  formatCompactCurrency: (value: number | null) => string;
  formatOptionalCurrency: (value: number | null) => string;
  formatPercentFromFraction: (value: number) => string;
};

export function PortfolioOverviewSection({
  data,
  isDark,
  compositionPositiveItems,
  compositionNegativeItems,
  compositionPositiveTotal,
  compositionNegativeTotal,
  compositionAbsTotal,
  compositionDonutBackground,
  compositionNegativeDonutBackground,
  onGoToCashTab,
  resolveCompositionHref,
  isCashCompositionItem,
  metricAccentColor,
  appColors,
  metricInfoText,
  normalizeMetricLabel,
  formatMetricValue,
  formatCompactCurrency,
  formatOptionalCurrency,
  formatPercentFromFraction,
}: Props) {
  const linkButtonSx = {
    textTransform: 'none',
    color: appColors.actions.accentOutline.text,
    borderColor: appColors.actions.accentOutline.border,
    '&:hover': {
      color: appColors.actions.accentOutline.hoverText,
      borderColor: appColors.actions.accentOutline.hoverBorder,
      bgcolor: appColors.actions.accentOutline.hoverBg,
    },
  } as const;
  const metricByLabel = new Map(data.metrics.map((metric) => [normalizeMetricLabel(metric.label), metric]));
  const getMetric = (label: string) => metricByLabel.get(normalizeMetricLabel(label));
  const metricPatrimonio = getMetric('Patrimônio líquido');
  const metricInvestido = getMetric('Total investido');
  const metricAtivo = getMetric('Valor do ativo');
  const metricPassivo = getMetric('A pagar') ?? getMetric('Total passivo');
  const topRowMetrics = [metricPatrimonio, metricInvestido].filter((metric): metric is PortfolioMetric =>
    Boolean(metric),
  );
  const bottomRowMetrics = [metricAtivo, metricPassivo].filter((metric): metric is PortfolioMetric => Boolean(metric));
  const fallbackMetrics = topRowMetrics.length === 0 && bottomRowMetrics.length === 0 ? data.metrics : [];

  const renderMetricCard = (metric: PortfolioMetric, keyPrefix: string) => (
    <Paper
      key={`${keyPrefix}-${metric.label}`}
      variant='outlined'
      sx={{
        p: 1.1,
        borderTop: `3px solid ${metricAccentColor(metric.label, appColors)}`,
        background: isDark
          ? 'linear-gradient(180deg, hsl(var(--card)) 0%, hsl(var(--muted) / 0.35) 100%)'
          : 'linear-gradient(180deg, hsl(var(--card)) 0%, hsl(var(--muted) / 0.22) 100%)',
      }}
    >
      <Stack direction='row' spacing={0.6} alignItems='center' sx={{ mb: 0.35 }}>
        <Typography variant='caption' color='text.secondary' sx={{ lineHeight: 1.2, fontWeight: 700 }}>
          {metric.label}
        </Typography>
        <InfoDotTooltip text={metricInfoText(metric.label)} />
      </Stack>
      <Typography variant='h6' fontWeight={900} sx={{ mt: 0.2, lineHeight: 1.2 }}>
        <Tooltip title={formatMetricValue(metric.value, metric.kind)} arrow>
          <Box component='span' sx={{ cursor: 'help' }}>
            {formatCompactCurrency(metric.value)}
          </Box>
        </Tooltip>
      </Typography>
    </Paper>
  );

  return (
    <Stack spacing={2}>
      {topRowMetrics.length > 0 ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 1.2,
          }}
        >
          {topRowMetrics.map((metric) => renderMetricCard(metric, 'top'))}
        </Box>
      ) : null}
      {bottomRowMetrics.length > 0 ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 1.2,
          }}
        >
          {bottomRowMetrics.map((metric) => renderMetricCard(metric, 'bottom'))}
        </Box>
      ) : null}
      {fallbackMetrics.length > 0 ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 1.2,
          }}
        >
          {fallbackMetrics.map((metric) => renderMetricCard(metric, 'fallback'))}
        </Box>
      ) : null}

      <Paper variant='outlined' sx={{ p: 1.5 }}>
        <Stack direction='row' spacing={0.6} alignItems='center' sx={{ mb: 1 }}>
          <Stack direction='row' spacing={0.6} alignItems='center'>
            <Typography variant='subtitle1' fontWeight={700}>
              Composição da carteira (valores absolutos)
            </Typography>
            <InfoDotTooltip text='Distribuição do patrimônio investido por classe de ativo no período de referência.' />
          </Stack>
        </Stack>
        {data.composition.length > 0 ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(2, minmax(0, 1fr))',
              },
              gap: 2,
              alignItems: 'start',
            }}
          >
            <Paper variant='outlined' sx={{ p: 1.4 }}>
              <Typography variant='subtitle2' fontWeight={700} sx={{ mb: 1 }}>
                Composição positiva (ativos)
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '180px 1fr' },
                  gap: 1.2,
                  alignItems: 'start',
                }}
              >
                <Stack spacing={1} alignItems='center'>
                  <Box
                    sx={{
                      width: 140,
                      height: 140,
                      borderRadius: '50%',
                      background: compositionDonutBackground,
                      display: 'grid',
                      placeItems: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        width: 84,
                        height: 84,
                        borderRadius: '50%',
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        display: 'grid',
                        placeItems: 'center',
                        textAlign: 'center',
                        px: 0.6,
                      }}
                    >
                      <Typography variant='caption' color='text.secondary'>
                        Total ativos
                      </Typography>
                      <Typography variant='subtitle2' fontWeight={800}>
                        <Tooltip title={formatOptionalCurrency(compositionPositiveTotal)} arrow>
                          <Box component='span' sx={{ cursor: 'help' }}>
                            {formatCompactCurrency(compositionPositiveTotal)}
                          </Box>
                        </Tooltip>
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
                <Stack spacing={0.8}>
                  {compositionPositiveItems.map((item) => {
                    const goesToCashTab = isCashCompositionItem(item.label);
                    const compositionHref = goesToCashTab ? null : resolveCompositionHref(item);
                    return (
                      <Stack key={item.label} spacing={0.35}>
                        <Stack direction='row' justifyContent='space-between'>
                          <Stack direction='row' spacing={0.8} alignItems='center'>
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: 999,
                                backgroundColor: item.color,
                              }}
                            />
                            <Typography variant='body2'>{item.label}</Typography>
                          </Stack>
                          <Stack direction='row' spacing={1} alignItems='center'>
                            <Typography variant='body2' color='text.secondary'>
                              {compositionPositiveTotal > 0
                                ? formatPercentFromFraction((item.absValue / compositionPositiveTotal) * 100)
                                : '-'}
                            </Typography>
                            <Typography variant='body2' fontWeight={700}>
                              <Tooltip title={formatOptionalCurrency(item.value)} arrow>
                                <Box component='span' sx={{ cursor: 'help' }}>
                                  {formatCompactCurrency(item.value)}
                                </Box>
                              </Tooltip>
                            </Typography>
                            {goesToCashTab ? (
                              <Button size='small' variant='outlined' onClick={onGoToCashTab} sx={linkButtonSx}>
                                Ver
                              </Button>
                            ) : compositionHref ? (
                              <AppLink
                                href={compositionHref}
                                underline='none'
                                sx={{
                                  ...linkButtonSx,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  px: 1.25,
                                  py: 0.4,
                                  borderRadius: 1.5,
                                  fontSize: 13,
                                  border: '1px solid',
                                  textTransform: 'none',
                                }}
                              >
                                Ver
                              </AppLink>
                            ) : null}
                          </Stack>
                        </Stack>
                        <LinearProgress
                          variant='determinate'
                          value={compositionPositiveTotal > 0 ? (item.absValue / compositionPositiveTotal) * 100 : 0}
                          sx={{
                            height: 8,
                            borderRadius: 999,
                            backgroundColor: 'hsl(var(--muted))',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: item.color,
                            },
                          }}
                        />
                      </Stack>
                    );
                  })}
                </Stack>
              </Box>
            </Paper>
            <Paper variant='outlined' sx={{ p: 1.4 }}>
              <Typography variant='subtitle2' fontWeight={700} sx={{ mb: 1 }}>
                Obrigações/passivos
              </Typography>
              {compositionNegativeItems.length > 0 ? (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '180px 1fr' },
                    gap: 1.2,
                    alignItems: 'start',
                  }}
                >
                  <Stack spacing={1} alignItems='center'>
                    <Box
                      sx={{
                        width: 140,
                        height: 140,
                        borderRadius: '50%',
                        background: compositionNegativeDonutBackground,
                        display: 'grid',
                        placeItems: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          width: 84,
                          height: 84,
                          borderRadius: '50%',
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          display: 'grid',
                          placeItems: 'center',
                          textAlign: 'center',
                          px: 0.6,
                        }}
                      >
                        <Typography variant='caption' color='text.secondary'>
                          Total passivos
                        </Typography>
                        <Typography variant='subtitle2' fontWeight={800} color='error.main'>
                          <Tooltip title={formatOptionalCurrency(compositionNegativeTotal)} arrow>
                            <Box component='span' sx={{ cursor: 'help' }}>
                              {formatCompactCurrency(compositionNegativeTotal)}
                            </Box>
                          </Tooltip>
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      size='small'
                      color='error'
                      variant='outlined'
                      label={`A pagar / total: ${formatPercentFromFraction(compositionAbsTotal > 0 ? (compositionNegativeTotal / compositionAbsTotal) * 100 : 0)}`}
                    />
                  </Stack>
                  <Stack spacing={0.8}>
                    {compositionNegativeItems.map((item) => {
                      const goesToCashTab = isCashCompositionItem(item.label);
                      const compositionHref = goesToCashTab ? null : resolveCompositionHref(item);
                      return (
                        <Stack key={item.label} spacing={0.35}>
                          <Stack direction='row' justifyContent='space-between'>
                            <Stack direction='row' spacing={0.8} alignItems='center'>
                              <Box
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: 999,
                                  backgroundColor: item.color,
                                }}
                              />
                              <Typography variant='body2'>{item.label}</Typography>
                            </Stack>
                            <Stack direction='row' spacing={1} alignItems='center'>
                              <Typography variant='body2' color='text.secondary'>
                                {compositionNegativeTotal > 0
                                  ? formatPercentFromFraction((item.absValue / compositionNegativeTotal) * 100)
                                  : '-'}
                              </Typography>
                              <Typography variant='body2' fontWeight={700} color='error.main'>
                                <Tooltip title={formatOptionalCurrency(item.value)} arrow>
                                  <Box component='span' sx={{ cursor: 'help' }}>
                                    {formatCompactCurrency(item.value)}
                                  </Box>
                                </Tooltip>
                              </Typography>
                              {goesToCashTab ? (
                                <Button size='small' variant='outlined' onClick={onGoToCashTab} sx={linkButtonSx}>
                                  Ver
                                </Button>
                              ) : compositionHref ? (
                                <AppLink
                                  href={compositionHref}
                                  underline='none'
                                  sx={{
                                    ...linkButtonSx,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    px: 1.25,
                                    py: 0.4,
                                    borderRadius: 1.5,
                                    fontSize: 13,
                                    border: '1px solid',
                                    textTransform: 'none',
                                  }}
                                >
                                  Ver
                                </AppLink>
                              ) : null}
                            </Stack>
                          </Stack>
                          <LinearProgress
                            variant='determinate'
                            value={compositionNegativeTotal > 0 ? (item.absValue / compositionNegativeTotal) * 100 : 0}
                            sx={{
                              height: 8,
                              borderRadius: 999,
                              backgroundColor: 'hsl(var(--muted))',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: item.color,
                              },
                            }}
                          />
                        </Stack>
                      );
                    })}
                  </Stack>
                </Box>
              ) : (
                <Typography variant='body2' color='text.secondary'>
                  Sem passivos relevantes nesta composição.
                </Typography>
              )}
            </Paper>
          </Box>
        ) : (
          <Typography variant='body2' color='text.secondary'>
            Sem dados de composição disponíveis para o período.
          </Typography>
        )}
      </Paper>
    </Stack>
  );
}
